import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Search, Plus, Send, X, Trash2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import InlineError from '../components/InlineError';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { toast } from 'sonner';
import './Messages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(iso) {
  if (!iso) return '';
  // Legacy/naive server strings (no Z / ±hh:mm) are UTC — mark them so the
  // Date is converted to the DEVICE timezone instead of parsed as local.
  const s = typeof iso === 'string' && !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso + 'Z' : iso;
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// Strip any string that looks like a raw ad identifier (e.g. "ad_e17881a98e09"
// or "Ad: ad_e17881a98e09") so internal IDs never leak into the UI. Callers
// should treat the empty return as "use localized fallback".
const RAW_AD_ID_RE = /^(ad:\s*)?ad_[a-z0-9]+$/i;
function sanitizeAdTitle(raw) {
  const s = (raw == null ? '' : String(raw)).trim();
  if (!s) return '';
  if (RAW_AD_ID_RE.test(s)) return '';
  return s;
}

// ── Global unread count (polled by BottomNav) ────────────────
let _cachedUnread = 0;
let _lastToken = null;

export function getUnreadCount() {
  return _cachedUnread;
}

async function _fetchUnread(token) {
  if (!token) { _cachedUnread = 0; return; }
  try {
    const r = await fetchWithTimeout(`${API_URL}/api/messages/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const d = await r.json();
      _cachedUnread = d.count || 0;
    }
  } catch { /* silent */ }
}

/** Messages Inbox Page */
export default function MessagesInbox() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const { user, token } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const [showNew, setShowNew] = useState(false);

  const fetchThreads = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoadError(false);
    try {
      const r = await fetchWithTimeout(`${API_URL}/api/messages/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setThreads(d.threads || []);
      } else {
        setLoadError(true);
      }
    } catch {
      setLoadError(true);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchThreads();
    // Also refresh unread for BottomNav
    _lastToken = token;
    _fetchUnread(token);
  }, [fetchThreads, token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return threads.filter(t => {
      if (!q) return true;
      const name = t.other_user?.name || '';
      const title = sanitizeAdTitle(t.ad_title);
      return name.toLowerCase().includes(q) || title.toLowerCase().includes(q);
    });
  }, [threads, query]);

  const openConv = (threadId) => {
    navigate(`/messages/${threadId}`);
  };

  // Soft-delete a thread for the current user. Single shared pipeline used by
  // both the inbox row and the chat-header overflow (see ChatPage below):
  // DELETE /api/messages/threads/{id} + optimistic local removal + unread
  // refresh. Cross-user safe: backend scopes the hide flag to the caller.
  const [pendingDelete, setPendingDelete] = useState(null); // { threadId }
  const confirmDeleteThread = useCallback(async () => {
    if (!pendingDelete?.threadId) return;
    const threadId = pendingDelete.threadId;
    setPendingDelete(null);
    try {
      const r = await fetchWithTimeout(`${API_URL}/api/messages/threads/${threadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('delete_failed');
      setThreads(prev => prev.filter(th => th.thread_id !== threadId));
      _fetchUnread(token);
      toast.success(t('mess.deleted'));
    } catch (e) {
      console.error('[Messages] delete failed', e);
      toast.error(t('mess.deleteFailed'));
    }
  }, [pendingDelete, token, t]);

  const createConv = async (title, subtitle) => {
    // When creating from the "New" button, we don't have a recipient_id
    // Show an info message — users should start conversations from ad pages
    navigate('/shop');
  };

  return (
    <div className="msg-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Shared AppHeader.
          By default this is a bottom-nav tab page (no back button).
          When the user arrives from /account or /admin/* (via route state
          `from`) we surface a Back arrow so they can return without losing
          context. EN/AR direction is handled by AppHeader. */}
      <AppHeader
        title={t('bottnav.messages')}
        showBack={!!location.state?.from}
        onBack={location.state?.from ? (() => navigate(location.state.from)) : undefined}
        testId="messages-header"
      />

      <div className="msg-toolbar">
        <div className="msg-search-wrap">
          <Search size={16} className="msg-search-icon" />
          <input
            className="msg-search"
            placeholder={t('mess.searchConversations')}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="msg-empty">
          <div className="msg-empty-title">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
        </div>
      ) : loadError && threads.length === 0 ? (
        <InlineError onRetry={() => { setLoading(true); fetchThreads(); }} />
      ) : filtered.length === 0 ? (
        <div className="msg-empty" data-testid="messages-empty">
          <MessageCircle size={40} className="msg-empty-icon" />
          <div className="msg-empty-title">{t('mess.noConversationsYet')}</div>
          <div className="msg-empty-text">{isRTL ? 'ابدأ محادثة من صفحة الإعلان' : 'Start a conversation from an ad page'}</div>
        </div>
      ) : (
        <div className="msg-list" data-testid="messages-list">
          {filtered.map(th => {
            const safeTitle = sanitizeAdTitle(th.ad_title);
            const name = th.other_user?.name || safeTitle || 'User';
            const avatar = name[0]?.toUpperCase() || '?';
            const preview = th.last_message_text || safeTitle || '';
            return (
              <div key={th.thread_id} className="msg-row-wrap">
                <button className="msg-row" onClick={() => openConv(th.thread_id)} data-testid={`conv-${th.thread_id}`}>
                  <div className="msg-avatar">{avatar}</div>
                  <div className="msg-row-body">
                    <div className="msg-row-top">
                      <span className="msg-row-name">{name}</span>
                      <span className="msg-row-time">{formatTime(th.last_message_at)}</span>
                    </div>
                    <div className="msg-row-bottom">
                      <span className="msg-row-preview">{preview}</span>
                      {th.unread_count > 0 && <span className="msg-unread-pill">{th.unread_count}</span>}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  className="msg-row-delete-btn"
                  aria-label={t('mess.deleteConversation')}
                  title={t('mess.deleteConversation')}
                  data-testid={`conv-delete-${th.thread_id}`}
                  onClick={(e) => { e.stopPropagation(); setPendingDelete({ threadId: th.thread_id }); }}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {pendingDelete && (
        <DeleteConfirmModal
          isRTL={isRTL}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDeleteThread}
        />
      )}
    </div>
  );
}

/** Chat Page */
export function ChatPage() {
  const { id: convId } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const routeState = routeLocation.state || {};
  const location = window.history.state?.usr || {};
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const pageRef = useRef(null);

  // Handle /messages/new — create thread from ad context
  const [resolvedThreadId, setResolvedThreadId] = useState(convId === 'new' ? null : convId);
  const [pendingDelete, setPendingDelete] = useState(false);
  // Guards the auto-intro POST against double-fire (StrictMode re-run,
  // dependency-identity re-renders, or fast repeated taps on "Chat in Wassla").
  const introSentRef = useRef(false);

  // Soft-delete this conversation (single source of truth: same backend
  // endpoint as the inbox row). Cross-user safe — the other participant
  // keeps seeing the thread unchanged.
  const confirmDeleteConversation = useCallback(async () => {
    if (!resolvedThreadId || !token) return;
    setPendingDelete(false);
    try {
      const r = await fetchWithTimeout(`${API_URL}/api/messages/threads/${resolvedThreadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('delete_failed');
      _fetchUnread(token);
      toast.success(t('mess.deleted'));
      navigate('/messages', { replace: true });
    } catch (e) {
      console.error('[Chat] delete failed', e);
      toast.error(t('mess.deleteFailed'));
    }
  }, [resolvedThreadId, token, t, navigate]);

  useEffect(() => {
    if (convId === 'new' && !resolvedThreadId && token) {
      const recipientId = routeState.sellerId || routeState.recipientId;
      const adId = routeState.fromAdId;
      if (recipientId) {
        if (introSentRef.current) return;
        introSentRef.current = true;
        // Send an initial system-like message to create the thread.
        // intro:true lets the backend skip it if an identical intro already
        // exists in the conversation (re-opening chat from the same listing).
        const adTitle = routeState.adTitle || '';
        const initText = adTitle
          ? (isRTL ? `مرحباً، أنا مهتم بإعلانك: ${adTitle}` : `Hi, I'm interested in your ad: ${adTitle}`)
          : (isRTL ? 'مرحباً' : 'Hi');

        fetch(`${API_URL}/api/messages/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ recipient_id: recipientId, ad_id: adId, text: initText, intro: true }),
        })
          .then(r => r.json())
          .then(d => {
            if (d.success && d.thread_id) {
              setResolvedThreadId(d.thread_id);
              navigate(`/messages/${d.thread_id}`, { replace: true, state: routeState });
            } else {
              introSentRef.current = false;
            }
          })
          .catch(() => { introSentRef.current = false; });
      } else {
        navigate('/messages', { replace: true });
      }
    }
  }, [convId, resolvedThreadId, routeState, token, navigate, isRTL]);

  // Fetch thread messages
  const fetchMessages = useCallback(async () => {
    if (!resolvedThreadId || resolvedThreadId === 'new' || !token) return;
    try {
      const r = await fetchWithTimeout(`${API_URL}/api/messages/threads/${resolvedThreadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setThread(d.thread);
        setMessages(d.messages || []);
        // Refresh unread count
        _fetchUnread(token);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [resolvedThreadId, token]);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Keyboard-aware viewport (Android). Two paths:
  // 1) Capacitor WebView with adjustResize + IME insets: the whole WebView
  //    shrinks, window.innerHeight tracks it, and the fixed inset:0 page
  //    resizes natively — we only re-pin the thread to the bottom.
  // 2) Mobile browser (visual viewport shrinks but layout viewport doesn't):
  //    pin the fixed page's height to the visible area so the composer sits
  //    directly above the keyboard instead of being covered by it.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      const page = pageRef.current;
      if (!page) return;
      const kbHeight = window.innerHeight - vv.height - vv.offsetTop;
      if (kbHeight > 60) {
        page.style.height = `${vv.height + vv.offsetTop}px`;
        page.classList.add('kb-open');
      } else {
        page.style.height = '';
        page.classList.remove('kb-open');
      }
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: 'end' }));
    };
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, []);

  // Source-aware back navigation — pop real history first; use the source
  // hints only when the chat was opened without in-app history (deep link).
  const handleBack = useCallback(() => {
    if (hasInAppHistory()) return navigate(-1);
    const fromBookId = routeState?.fromBookId || location?.fromBookId;
    const fromAdId = routeState?.fromAdId || location?.fromAdId;
    if (fromBookId) {
      navigate(`/student-hub/books/${fromBookId}`, { replace: true });
    } else if (fromAdId) {
      navigate(`/ad/${fromAdId}`, { replace: true });
    } else {
      navigate('/messages', { replace: true });
    }
  }, [navigate, location, routeState]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || !resolvedThreadId || !token) return;

    // Optimistic update
    const optimisticMsg = {
      message_id: `opt_${Date.now()}`,
      thread_id: resolvedThreadId,
      sender_id: user?.user_id,
      text: trimmed,
      read: false,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setText('');

    try {
      await fetchWithTimeout(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ thread_id: resolvedThreadId, text: trimmed }),
      });
    } catch { /* optimistic UI already updated */ }
  };

  const convTitle = thread?.other_user?.name || sanitizeAdTitle(thread?.ad_title) || '';
  // Human-readable ad context for the chat subtitle. If the thread has no
  // resolvable title (ad deleted or linked to none), fall back to a localized
  // "Ad" label — never show the raw ad_id.
  const safeAdTitle = sanitizeAdTitle(thread?.ad_title);
  const adSubtitle = thread?.ad_id
    ? (safeAdTitle || (isRTL ? 'إعلان' : 'Ad'))
    : '';

  if (loading && !thread) {
    return (
      <div className="msg-page">
        <div className="msg-empty">
          <div className="msg-empty-title">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
        </div>
      </div>
    );
  }

  if (!thread && !loading) {
    return (
      <div className="msg-page">
        <div className="msg-empty">
          <div className="msg-empty-title">{t('mess.conversationNotFound')}</div>
          <button className="msg-back-link" onClick={() => navigate('/messages')}>{t('mess.backToMessages')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page" ref={pageRef} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Shared AppHeader — back + conversation title + ad subtitle +
          delete action. All three live as flex children so the delete
          button can never overlap the title or the back arrow. */}
      <AppHeader
        onBack={handleBack}
        testId="chat-header"
        titleSlot={
          <>
            <h1 className="app-header__title" data-testid="app-header-title">
              {convTitle}
            </h1>
            {adSubtitle && (
              thread?.ad_id ? (
                <button
                  type="button"
                  className="chat-header-sub chat-header-sub-link"
                  onClick={() => navigate(`/ad/${thread.ad_id}`)}
                  data-testid="chat-header-ad-link"
                  title={adSubtitle}
                >
                  {adSubtitle}
                </button>
              ) : (
                <div className="chat-header-sub" data-testid="chat-header-ad">
                  {adSubtitle}
                </div>
              )
            )}
          </>
        }
        rightSlot={
          <button
            type="button"
            className="chat-delete-btn"
            onClick={() => setPendingDelete(true)}
            aria-label={t('mess.deleteConversation')}
            title={t('mess.deleteConversation')}
            data-testid="chat-delete-btn"
          >
            <Trash2 size={20} />
          </button>
        }
      />

      {pendingDelete && (
        <DeleteConfirmModal
          isRTL={isRTL}
          onCancel={() => setPendingDelete(false)}
          onConfirm={confirmDeleteConversation}
        />
      )}

      <div className="chat-body">
        {messages.map(m => {
          const isMe = m.sender_id === user?.user_id;
          if (m.sender_id === 'system') {
            return (
              <div key={m.message_id} className="chat-system-row" data-testid="chat-system-msg">
                <div className="chat-system-bubble">
                  <span className="chat-system-text">{m.text}</span>
                </div>
              </div>
            );
          }
          return (
            <div key={m.message_id} className={`chat-bubble-row ${isMe ? 'chat-row-me' : 'chat-row-them'}`}>
              <div className={`chat-bubble ${isMe ? 'chat-bubble-me' : 'chat-bubble-them'}`}>
                <div className="chat-bubble-text">{m.text}</div>
                <div className="chat-bubble-time">{formatTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="chat-input-bar">
        <input
          className="chat-input"
          placeholder={t('mess.typeAMessage')}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          onFocus={() => setTimeout(() => endRef.current?.scrollIntoView({ block: 'end' }), 250)}
          data-testid="chat-input"
        />
        <button className="chat-send-btn" onClick={send} data-testid="send-message-btn">
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

/** Shared confirmation modal used by inbox row and chat header. */
function DeleteConfirmModal({ isRTL, onCancel, onConfirm, titleKey = 'mess.deleteConversationTitle', bodyKey = 'mess.deleteConversationBody' }) {
  const { t } = useTranslation();
  return (
    <div className="msg-modal-overlay" onClick={onCancel} data-testid="delete-confirm-overlay">
      <div className="msg-modal msg-modal--confirm" onClick={e => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="msg-modal-body">
          <h3 className="msg-modal-confirm-title" data-testid="delete-confirm-title">{t(titleKey)}</h3>
          <p className="msg-modal-confirm-body" data-testid="delete-confirm-body">{t(bodyKey)}</p>
          <div className="msg-modal-actions">
            <button className="msg-modal-cancel" onClick={onCancel} data-testid="delete-confirm-cancel">{t('mess.cancel')}</button>
            <button className="msg-modal-delete" onClick={onConfirm} data-testid="delete-confirm-ok">{t('mess.delete')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** New Conversation Modal (kept for backward compat but currently unused) */
function NewConvModal({ onClose, onCreate, isRTL }) {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');

  return (
    <div className="msg-modal-overlay" onClick={onClose}>
      <div className="msg-modal" onClick={e => e.stopPropagation()}>
        <div className="msg-modal-header">
          <span className="msg-modal-title">{isRTL ? 'محادثة جديدة' : 'New Conversation'}</span>
          <button className="msg-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="msg-modal-body">
          <label className="msg-label">{isRTL ? 'الاسم' : 'Name'}</label>
          <input className="msg-modal-input" value={title} onChange={e => setTitle(e.target.value)} />
          <label className="msg-label">{isRTL ? 'الموضوع' : 'Topic'}</label>
          <input className="msg-modal-input" value={subtitle} onChange={e => setSubtitle(e.target.value)} />
          <div className="msg-modal-actions">
            <button className="msg-modal-cancel" onClick={onClose}>{isRTL ? 'إلغاء' : 'Cancel'}</button>
            <button className="msg-modal-create" disabled={!title.trim()} onClick={() => title.trim() && onCreate(title, subtitle)}>
              {isRTL ? 'إنشاء' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
